import { pinnedModelIdSchema } from '../models.js';
import { utcInstantSchema } from '../content/common.js';
import { z } from 'zod';
import { supportContractLimits, supportOpaqueIdSchema, supportRequestCategorySchema, supportRequestEventTypeSchema, supportRequestStatusSchema, supportSideEffectStatusSchema, supportTriagePrioritySchema, } from './common.js';
import { registrationSupportSummarySchema } from './registration.js';
export const supportStaffSummarySchema = z
    .object({
    id: z.number().int().positive(),
    name: z.string().trim().min(1).max(supportContractLimits.name),
})
    .strict();
export const supportSideEffectOutcomeSchema = z
    .object({
    status: supportSideEffectStatusSchema,
    code: z.string().trim().min(1).max(supportContractLimits.sideEffectCode).nullable(),
    message: z.string().trim().min(1).max(supportContractLimits.sideEffectMessage).nullable(),
    updatedAt: utcInstantSchema.nullable(),
    /** Derived operational state; the stored vocabulary remains disabled|pending|succeeded|failed. */
    interrupted: z.boolean().default(false),
})
    .strict();
export const supportTriageOutcomeSchema = supportSideEffectOutcomeSchema
    .extend({
    model: pinnedModelIdSchema.nullable(),
    priority: supportTriagePrioritySchema.nullable(),
    summary: z.string().trim().min(1).max(supportContractLimits.triageSummary).nullable(),
    reason: z.string().trim().min(1).max(supportContractLimits.triageReason).nullable(),
})
    .strict();
export const supportRequestQueueItemSchema = z
    .object({
    publicId: supportOpaqueIdSchema,
    category: supportRequestCategorySchema,
    status: supportRequestStatusSchema,
    createdAt: utcInstantSchema,
    updatedAt: utcInstantSchema,
    assignedTo: supportStaffSummarySchema.nullable(),
    customerName: z.string().trim().min(1).max(supportContractLimits.name).nullable(),
    redactedAt: utcInstantSchema.nullable(),
    requiresRetentionReview: z.boolean(),
    triageStatus: supportSideEffectStatusSchema,
    notificationStatus: supportSideEffectStatusSchema,
    triageInterrupted: z.boolean(),
    notificationInterrupted: z.boolean(),
})
    .strict();
export const supportRequestQueueItemFields = Object.freeze(Object.keys(supportRequestQueueItemSchema.shape));
const assigneeFilterSchema = z.union([z.literal('unassigned'), z.coerce.number().int().positive()]);
export const supportRequestListQuerySchema = z
    .object({
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().min(1).max(supportContractLimits.pageSize).default(25),
    status: supportRequestStatusSchema.optional(),
    category: supportRequestCategorySchema.optional(),
    assignee: assigneeFilterSchema.optional(),
    search: z.string().trim().min(1).max(supportContractLimits.search).optional(),
})
    .strict();
export const supportRequestListSchema = z
    .object({
    items: z.array(supportRequestQueueItemSchema),
    page: z.number().int().positive(),
    pageSize: z.number().int().min(1).max(supportContractLimits.pageSize),
    total: z.number().int().nonnegative(),
    totalPages: z.number().int().nonnegative(),
})
    .strict();
export const supportRequestNoteSchema = z
    .object({
    body: z.string().trim().min(1).max(supportContractLimits.note),
    author: supportStaffSummarySchema,
    createdAt: utcInstantSchema,
})
    .strict();
export const supportRequestEventSchema = z
    .object({
    type: supportRequestEventTypeSchema,
    actor: supportStaffSummarySchema.nullable(),
    from: z.string().max(supportContractLimits.eventValue).nullable(),
    to: z.string().max(supportContractLimits.eventValue).nullable(),
    detail: z.string().max(supportContractLimits.eventDetail).nullable(),
    createdAt: utcInstantSchema,
})
    .strict();
export const supportRetentionHoldSchema = z
    .object({
    until: utcInstantSchema,
    reason: z.string().trim().min(1).max(supportContractLimits.retentionHoldReason),
    recordedBy: supportStaffSummarySchema,
    recordedAt: utcInstantSchema,
})
    .strict();
export const registrationSupportMatchStates = ['none', 'single', 'multiple'];
export const registrationSupportMatchStateSchema = z.enum(registrationSupportMatchStates);
export const registrationSupportIdentityNotice = 'Registration matches are support candidates only and are not proof of identity.';
export const registrationSupportIdentityNoticeSchema = z.literal(registrationSupportIdentityNotice);
export const supportRequestDetailSchema = supportRequestQueueItemSchema
    .extend({
    email: z
        .string()
        .trim()
        .toLowerCase()
        .max(supportContractLimits.email)
        .pipe(z.email())
        .nullable(),
    message: z.string().max(supportContractLimits.messageMax).nullable(),
    resolvedAt: utcInstantSchema.nullable(),
    notes: z.array(supportRequestNoteSchema),
    events: z.array(supportRequestEventSchema),
    triage: supportTriageOutcomeSchema,
    notification: supportSideEffectOutcomeSchema,
    retentionHold: supportRetentionHoldSchema.nullable(),
    registrationMatchState: registrationSupportMatchStateSchema,
    registrationIdentityNotice: registrationSupportIdentityNoticeSchema,
    registrationMatches: z
        .array(registrationSupportSummarySchema)
        .max(supportContractLimits.registrationMatches),
})
    .strict()
    .superRefine((value, context) => {
    const expected = value.registrationMatches.length === 0
        ? 'none'
        : value.registrationMatches.length === 1
            ? 'single'
            : 'multiple';
    if (value.registrationMatchState !== expected) {
        context.addIssue({
            code: 'custom',
            path: ['registrationMatchState'],
            message: 'Registration match state must describe the candidate count',
        });
    }
});
export const supportRequestDetailFields = Object.freeze(Object.keys(supportRequestDetailSchema.shape));
export const supportStatusChangeSchema = z.object({ status: supportRequestStatusSchema }).strict();
export const supportAssignmentChangeSchema = z
    .object({ assigneeId: z.number().int().positive().nullable() })
    .strict();
export const supportStatusTransitionSchema = z
    .object({
    status: supportRequestStatusSchema,
    resolvedAt: utcInstantSchema.nullable(),
    event: supportRequestEventSchema.nullable(),
})
    .strict();
export const supportAssignmentTransitionSchema = z
    .object({
    assignedTo: supportStaffSummarySchema.nullable(),
    event: supportRequestEventSchema.nullable(),
})
    .strict();
export const supportRetentionHoldTransitionSchema = z
    .object({
    retentionHold: supportRetentionHoldSchema.nullable(),
    event: supportRequestEventSchema.nullable(),
})
    .strict();
export const supportNoteInputSchema = z
    .object({ body: z.string().trim().min(1).max(supportContractLimits.note) })
    .strict();
export const supportRetentionHoldInputSchema = z
    .object({
    until: utcInstantSchema.nullable(),
    reason: z.string().trim().min(1).max(supportContractLimits.retentionHoldReason).nullable(),
})
    .strict()
    .superRefine((value, context) => {
    if ((value.until === null) !== (value.reason === null)) {
        const message = 'Retention hold expiry and reason must both be present or both be null';
        context.addIssue({ code: 'custom', path: ['until'], message });
        context.addIssue({ code: 'custom', path: ['reason'], message });
    }
});
//# sourceMappingURL=admin.js.map